import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { InnerPageLayout, InnerCTA } from "./InnerPageLayout";
import { CASE_STUDIES } from "../data/pages";
import { Seo } from "../components/site/Seo";

export const CaseStudyPage = () => {
  const { slug } = useParams();
  const data = CASE_STUDIES[slug];
  if (!data) return <Navigate to="/work" replace />;

  const others = Object.values(CASE_STUDIES).filter((c) => c.slug !== slug);

  return (
    <InnerPageLayout title={data.h1}>
      <Seo
        title={data.h1}
        description={data.summary}
        path={`/work/${slug}`}
        image={data.image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: data.h1,
          description: data.summary,
          publisher: { "@type": "Organization", name: "Techovarya" },
        }}
      />
      {/* Hero with image */}
      <section
        id="top"
        data-testid="case-study-hero"
        className="relative bg-[#050505] pt-16 md:pt-24 pb-16 md:pb-20"
      >
        <div className="absolute inset-0 bg-grid opacity-30" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-8">
            <Link to="/" className="hover:text-[#FF5F1F] transition-colors">
              Home
            </Link>
            <span className="text-zinc-700">/</span>
            <Link to="/work" className="hover:text-[#FF5F1F] transition-colors">
              Work
            </Link>
            <span className="text-zinc-700">/</span>
            <span>{data.industry}</span>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              Case Study — {data.industry}
            </span>
            {data.badge && (
              <span
                data-testid="case-study-badge"
                className="inline-flex items-center gap-1 bg-[#FF5F1F] text-black font-mono text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2l2.4 4.9L20 8l-4 3.9.9 5.5L12 14.8 7.1 17.4 8 11.9 4 8l5.6-1.1L12 2z"/></svg>
                {data.badge}
              </span>
            )}
          </div>

          <h1
            data-testid="case-study-h1"
            className="font-headings text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[1.02] text-white max-w-5xl"
          >
            {data.h1}
          </h1>

          <p className="mt-8 max-w-3xl text-base md:text-lg text-zinc-400 leading-relaxed">
            {data.summary}
          </p>

          {/* Hero image */}
          <div className="mt-12 md:mt-16 relative overflow-hidden border border-white/10 aspect-[16/8] bg-black">
            <img
              src={data.image}
              alt={data.h1}
              className="w-full h-full object-cover opacity-70 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>

          {/* Meta bar */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {data.meta.map(([k, v], i) => (
              <div key={i} className="bg-[#0A0A0A] p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F] mb-2">
                  {k}
                </div>
                <div className="font-headings text-sm md:text-base font-semibold text-white leading-tight">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge */}
      <section
        data-testid="case-challenge"
        className="relative bg-[#0A0A0A] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#FF5F1F]" />
              <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                The Challenge
              </span>
            </div>
            <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] text-white">
              What we were asked to solve.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-base md:text-lg text-zinc-300 leading-relaxed">{data.challenge}</p>

            <div className="mt-10 flex flex-wrap gap-2">
              {data.capabilities.map((cap, i) => (
                <span
                  key={i}
                  className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border border-white/15 text-zinc-300"
                >
                  {cap}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section
        data-testid="case-approach"
        className="relative bg-[#050505] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              Our Approach
            </span>
          </div>
          <h2 className="font-headings text-3xl md:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05] max-w-3xl mb-12 md:mb-16">
            How we engineered the solution.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {data.approach.map(([title, desc], i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.1 }}
                className="bg-[#0A0A0A] p-6 md:p-8 flex flex-col gap-4"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-headings text-xl md:text-2xl font-bold text-white tracking-tight">
                  {title}
                </h3>
                <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section
        data-testid="case-outcomes"
        className="relative bg-[#0A0A0A] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              Outcomes
            </span>
          </div>
          <h2 className="font-headings text-3xl md:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05] max-w-3xl mb-12">
            What changed for the business.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.outcomes.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.1 }}
                className="border border-white/10 bg-[#050505] p-6 md:p-8 flex items-start gap-4"
              >
                <div className="font-headings text-3xl md:text-4xl font-black text-[#FF5F1F] leading-none">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-base md:text-lg text-zinc-200 leading-relaxed">{o}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Other case studies */}
      <section
        data-testid="other-work"
        className="relative bg-[#050505] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#FF5F1F]" />
                <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                  More Work
                </span>
              </div>
              <h2 className="font-headings text-3xl md:text-4xl font-black text-white">
                Other case studies
              </h2>
            </div>
            <Link
              to="/work"
              data-testid="back-to-work"
              className="hidden md:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 hover:text-[#FF5F1F] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              All Work
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/work/${c.slug}`}
                data-testid={`other-${c.slug}`}
                className="group relative overflow-hidden border border-white/10 bg-black min-h-[320px] flex flex-col"
              >
                <div className="absolute inset-0">
                  <img
                    src={c.image}
                    alt={c.h1}
                    className="w-full h-full object-cover opacity-40 group-hover:opacity-60 grayscale group-hover:grayscale-0 transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
                </div>
                <div className="relative z-10 mt-auto p-6 md:p-8">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
                    {c.industry}
                  </span>
                  <h3 className="mt-3 font-headings text-xl md:text-2xl font-bold text-white tracking-tight leading-tight">
                    {c.h1}
                  </h3>
                  <div className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white group-hover:text-[#FF5F1F] transition-colors">
                    Read case study <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <InnerCTA
        heading="Facing a Similar Engineering Challenge?"
        copy="Bring us the context of your product, workflow, or technical constraint — we will help identify the right path forward."
        cta="Talk to Our Team"
        testid="final-cta"
      />
    </InnerPageLayout>
  );
};
